import { Injectable } from "@angular/core";
import { ArrayHelper, Tools } from "@comunicare/comunicare-helper";
import {
  IAddPatientData,
  IConfiguration,
  IFilter,
  IPatient,
  IPatientDataSourceBackendParam,
  IPatientHistoric,
  IPatientInfo,
  IPatientObservation,
  IPatientQuestionnaire,
  IPatientsListInfo,
  IPatientUser,
  IReference,
  ITiming,
  STATUS_ENTITY,
} from "@comunicare/comunicare-interfaces";
import { Observable } from "rxjs";
import { concatMap, first, map } from "rxjs/operators";
import { IPatientDeactivation, IPatientDeactivationUpdate } from "src/app/models/patient-deactivation.model";
import { PreferenceService } from "../preference.service";
import { ApiService } from "./api.service";
import { BaseApiService } from "./baseApi.service";

// Above this number of services, we don't send them in the URL at all: the backend then falls back to
// all the services available to the requester, which avoids building an excessively long query string.
const MAX_SERVICES_IN_URL = 10;

@Injectable({
  providedIn: "root",
})
export class PatientApiService extends BaseApiService {
  constructor(private api: ApiService, private preferenceService: PreferenceService) {
    super(
      "Patients", // Theme !
      ["patient", "getPatientUsers"], // Post route paths
      [
        "dashboard/patientsInfos", // 0
        "patientUser", // 1
        "dashboard/patients/lastActivityInfos", // 2
        "patientsInfos", // 3
        "nbPatientsInfos", // 4
        "patient/historic", // 5
        "dashboard/patientObservationDefinitions", // 6
        "dashboard/patientQuestionnaires", // 7
        "dashboard/patients/lastActivityInfos/count", // 8
      ], // Get route paths
      [
        "patient", // 0
        "anonymousPatient", // 1
        "deactivatePatient", // 2
        "activatePatient", // 3
        "dashboard/configurationPatient", // 4
        "patient/retrievedIdentity", // 5
        "dashboard/obsParamsFreqConfig", // 6
        "dashboard/resetAuthCode", // 7
        "dashboard/questParamsFreqConfig", // 8
        "patient/deactivation/temporary/status", // 9
      ], // Update route paths
      ["patient"] // Delete route paths
    );
  }

  public listRefsByHealthcareService(
    healthcareService: string,
    services?: string[],
    monitoringServicesIds?: string[]
  ): Observable<IReference[]> {
    const params: {
      healthcareService?: string;
      services?: string;
      monitoringServicesIds?: string;
    } = { healthcareService };
    if (Tools.isDefined(services) && !ArrayHelper.isEmpty(services)) {
      params.services = JSON.stringify(services);
    }
    if (Tools.isDefined(monitoringServicesIds) && !ArrayHelper.isEmpty(monitoringServicesIds)) {
      params.monitoringServicesIds = monitoringServicesIds.join();
    }
    return this.api.get("dashboard/patientsRefs", params) as Observable<IReference[]>;
  }

  public listPatientInfo(
    services: string[],
    criteria: string,
    limit: number,
    monitoringServicesIds?: string[]
  ): Observable<IPatientInfo[]> {
    const params: {
      services?: string;
      monitoringServicesIds?: string;
      criteria?: string;
      limit?: number;
    } = { criteria, limit };
    if (Tools.isDefined(services) && !ArrayHelper.isEmpty(services)) {
      params.services = JSON.stringify(services);
    }
    if (Tools.isDefined(monitoringServicesIds) && !ArrayHelper.isEmpty(monitoringServicesIds)) {
      params.monitoringServicesIds = monitoringServicesIds.join();
    }
    return this.api.get(this.readRoutes[0], params) as Observable<IPatientInfo[]>;
  }

  public update(patient: IPatient, isAnonymous = false): Observable<IPatient> {
    return isAnonymous
      ? (this.api.put(this.updateRoutes[1], patient) as Observable<IPatient>)
      : (this.api.put(this.updateRoutes[0], patient) as Observable<IPatient>);
  }

  public deactivatePatient(patientId: string, deactivation?: IPatientDeactivation): Observable<unknown> {
    const body = {
      patientId,
      disableReason: deactivation?.reason,
      disableReasonDetail: deactivation?.reasonDetail,
      // A deactivation with no known type is a permanent one, as it was before temporary ones existed.
      permanent: deactivation?.permanent ?? true,
      date: deactivation?.date,
      predictedEndDate: deactivation?.predictedEndDate,
    };
    return this.api.put(this.updateRoutes[2], body);
  }

  /**
   * Changes what an ongoing temporary deactivation is made of, without ending it.
   * @param patientId the ID of the patient record - not the caremateId.
   */
  public updateDeactivationStatus(patientId: string, update: IPatientDeactivationUpdate): Observable<unknown> {
    const body = {
      patientId,
      subStatus: update.subStatus,
      predictedEndDate: update.predictedEndDate,
      disableReasonDetail: update.reasonDetail,
    };
    return this.api.put(this.updateRoutes[9], body);
  }

  public activatePatient(patientId: string): Observable<IPatient> {
    const body = { patientId };
    return this.api.put(this.updateRoutes[3], body) as Observable<IPatient>;
  }

  public removeForEver(patientId: string): Observable<IPatient> {
    return this.api.deleteWithParams(this.deleteRoutes[0], {
      patientId,
    }) as Observable<IPatient>;
  }

  public create(patient: IAddPatientData, reqParams: Record<string, string | boolean>): Observable<IPatient> {
    return this.api.post(this.createRoutes[0], patient, reqParams) as Observable<IPatient>;
  }

  public getPatientUser(caremateIdentifier: string): Observable<IPatientUser> {
    return this.api.get(this.readRoutes[1], {
      caremateIdentifier,
    }) as Observable<IPatientUser>;
  }

  public getPatientUsers(patientIds: string[]): Observable<IPatientUser[]> {
    return this.api.post(this.createRoutes[1], patientIds) as Observable<IPatientUser[]>;
  }

  public findPatients(params: IPatientDataSourceBackendParam): Observable<IPatientsListInfo[]> {
    return this.api.get(this.readRoutes[3], {
      pageNumber: params.pageNumber,
      pageSize: params.pageSize,
      search: params.search,
      withInactive: params.withInactive,
      services: JSON.stringify(params.services),
      sortId: params.sortId,
      sortOrder: params.sortOrder,
      lang: params.lang,
      filters: JSON.stringify(params.filters),
      groupId: params.groupId,
      period: params.period,
      monitoringServicesIds: params.monitoringServicesIds?.join(),
      insNumber: params.insNumber,
      patientDataPeriod: params.patientDataPeriod,
    }) as Observable<IPatientsListInfo[]>;
  }

  public getPatientsCount(
    services?: string[],
    search?: string,
    filters?: IFilter[],
    withInactive?: boolean,
    groupId?: string,
    period?: string,
    monitoringServicesIds?: string[],
    insNumber?: string
  ): Observable<{ count: number }> {
    const params: {
      search?: string;
      withInactive?: boolean;
      services?: string;
      monitoringServicesIds?: string;
      filters?: string;
      groupId?: string;
      period?: string;
      insNumber?: string;
    } = {
      search,
      withInactive,
      groupId,
      period,
      insNumber,
    };
    if (Tools.isDefined(services) && !ArrayHelper.isEmpty(services)) {
      params.services = JSON.stringify(services);
    }
    if (Tools.isDefined(monitoringServicesIds) && !ArrayHelper.isEmpty(monitoringServicesIds)) {
      params.monitoringServicesIds = monitoringServicesIds.join();
    }
    if (Tools.isDefined(filters) && !ArrayHelper.isEmpty(filters)) {
      params.filters = JSON.stringify(filters);
    }
    return this.api.get(this.readRoutes[4], params) as Observable<{
      count: number;
    }>;
  }

  /**
   * Resolves the inactivity period to use for the last-activity infos routes from the global
   * preference "inactifPatientDuration", the same way the deprecated listInactivePatient used to.
   */
  private getInactivityPeriod(): Observable<string> {
    return this.preferenceService.getGlobal().pipe(
      first(),
      map((parameters) => (parameters?.inactifPatientDuration ? parameters.inactifPatientDuration : "1 week"))
    );
  }

  public getLastActivityInfos(params: IPatientDataSourceBackendParam): Observable<IPatient[]> {
    return this.getInactivityPeriod().pipe(
      concatMap(
        (period) =>
          this.api.get(this.readRoutes[2], {
            pageNumber: params.pageNumber,
            pageSize: params.pageSize,
            period,
            services: params.services?.length > MAX_SERVICES_IN_URL ? undefined : JSON.stringify(params.services),
            monitoringServicesIds: params.monitoringServicesIds?.join(),
            sortId: params.sortId,
            sortOrder: params.sortOrder,
            filters: JSON.stringify(params.filters),
          }) as Observable<IPatient[]>
      )
    );
  }

  public getLastActivityInfosCount(params: IPatientDataSourceBackendParam): Observable<{ count: number }> {
    return this.getInactivityPeriod().pipe(
      concatMap(
        (period) =>
          this.api.get(this.readRoutes[8], {
            period,
            services: params.services?.length > MAX_SERVICES_IN_URL ? undefined : JSON.stringify(params.services),
            monitoringServicesIds: params.monitoringServicesIds?.join(),
            filters: JSON.stringify(params.filters),
          }) as Observable<{ count: number }>
      )
    );
  }

  public getPatientHistoric(patientId: string, organizationId: string): Observable<IPatientHistoric[]> {
    return this.api.get(this.readRoutes[5], {
      patientId,
      organizationId,
    }) as Observable<IPatientHistoric[]>;
  }

  public getPatientObservationDefinitions(identifier: string, language?: string): Observable<IPatientObservation> {
    return this.api.get(this.readRoutes[6], {
      identifier,
      language,
    }) as Observable<IPatientObservation>;
  }
  public getPatientQuestionnaires(identifier: string, language?: string): Observable<IPatientQuestionnaire> {
    return this.api.get(this.readRoutes[7], {
      identifier,
      language,
    }) as Observable<IPatientQuestionnaire>;
  }

  public updatePatientConfiguration(identifier: string, code?: string[], questionnairesIds?: string[]): Observable<unknown> {
    const body = {
      caremateId: identifier,
      code,
      questionnairesIds,
    };
    return this.api.put(this.updateRoutes[4], body);
  }

  public updatePatientObsParamsTiming(
    caremateId: string,
    obsLoinc: string,
    timing: ITiming,
    timings: ITiming[],
    entityStatus: STATUS_ENTITY,
    obsRef: string
  ): Observable<unknown> {
    const body = {
      caremateId,
      obsLoinc,
      timing,
      timings,
      entityStatus,
      obsRef,
    };
    return this.api.put(this.updateRoutes[6], body);
  }

  public updatePatientQuestParamsTiming(
    caremateId: string,
    questId: string,
    timing: ITiming,
    timings: ITiming[],
    entityStatus: STATUS_ENTITY
  ): Observable<unknown> {
    const body = {
      caremateId,
      questId,
      timing,
      timings,
      entityStatus,
    };
    return this.api.put(this.updateRoutes[8], body);
  }

  public getPatientConfiguration(caremateId: string): Observable<IConfiguration> {
    return this.api.get("dashboard/configuration", {
      identifier: caremateId,
    }) as Observable<IConfiguration>;
  }

  public resetAuthCode(patientId: string): Observable<unknown> {
    const body = {
      patientId,
    };
    return this.api.put(this.updateRoutes[7], body);
  }
}
